import type { TypographyVariantsOptions } from '@mui/material/styles';

import { pxToRem, setFont } from 'minimal-shared/utils';

import { themeConfig } from '../theme-config';

// ----------------------------------------------------------------------

/**
 * TypeScript extension for MUI theme augmentation.
 * @to {@link file://./../extend-theme-types.d.ts}
 */

export type TypographyVariantsExtend = {
  fontWeightSemiBold: React.CSSProperties['fontWeight'];
  fontWeightExtraBold: React.CSSProperties['fontWeight'];
  fontSecondaryFamily: React.CSSProperties['fontFamily'];
};

// ----------------------------------------------------------------------

const primaryFont = setFont(themeConfig.fontFamily.primary);
const secondaryFont = setFont(themeConfig.fontFamily.secondary);

const baseTypography: TypographyVariantsOptions = {
  fontFamily: primaryFont,
  fontSecondaryFamily: secondaryFont,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  fontWeightExtraBold: 800,
};

/* **********************************************************************
 * 📦 Final — Aligned with Figma design system (Inter)
 *
 * Figma mapping:
 *   heading 1        → h1    | heading 2       → h2
 *   heading 3        → h3    | heading 4       → h4
 *   paragraph bold   → subtitle1 (600)  | paragraph medium → subtitle2 (500)
 *   paragraph regular→ body1 | paragraph small → body2
 *   paragraph mini   → caption
 *   paragraph mini bold → overline (600, uppercase)
 * **********************************************************************/
export const typography: TypographyVariantsOptions = {
  ...baseTypography,
  h1: {
    fontWeight: baseTypography.fontWeightSemiBold,
    lineHeight: 1,
    letterSpacing: pxToRem(-1.5),
    fontSize: pxToRem(48),
  },
  h2: {
    fontWeight: baseTypography.fontWeightSemiBold,
    lineHeight: 1,
    letterSpacing: pxToRem(-1),
    fontSize: pxToRem(30),
  },
  h3: {
    fontWeight: baseTypography.fontWeightSemiBold,
    lineHeight: 1.2,
    letterSpacing: pxToRem(-1),
    fontSize: pxToRem(24),
  },
  h4: {
    fontWeight: baseTypography.fontWeightSemiBold,
    lineHeight: 1.2,
    fontSize: pxToRem(20),
  },
  h5: {
    fontWeight: baseTypography.fontWeightMedium,
    lineHeight: 1.2,
    fontSize: pxToRem(18),
  },
  h6: {
    fontWeight: baseTypography.fontWeightMedium,
    lineHeight: 1.2,
    fontSize: pxToRem(16),
  },
  subtitle1: {
    fontWeight: baseTypography.fontWeightSemiBold,
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  subtitle2: {
    fontWeight: baseTypography.fontWeightMedium,
    lineHeight: 1.5,
    fontSize: pxToRem(14),
  },
  body1: {
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  body2: {
    lineHeight: 1.5,
    fontSize: pxToRem(14),
  },
  caption: {
    lineHeight: 16 / 12,
    fontSize: pxToRem(12),
  },
  overline: {
    fontWeight: baseTypography.fontWeightSemiBold,
    lineHeight: 16 / 12,
    fontSize: pxToRem(12),
    textTransform: 'uppercase',
  },
  button: {
    fontWeight: baseTypography.fontWeightSemiBold,
    lineHeight: 24 / 14,
    fontSize: pxToRem(14),
    textTransform: 'unset',
  },
};
